import { showErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { BaseWidgetEditorComponent } from '@juvare/widget-ui/BaseWidgetEditorComponent';
import { getErrorMessage } from '@juvare/widget-ui/Utils';
import ImageIcon from '@mui/icons-material/Image';
import { Box, Button, FormControl, FormLabel } from '@mui/material';
import React from 'react';
import { buildEventTab, buildGeneralTab, buildHiddenTab, buildStyleTab, buildValidateTab, showWidgetEditDialog } from '../WidgetEditDialog';
import { FormHelperErrorDescription, FormTooltip } from '../WidgetForm/Components';
import { defaultWidgetLabelProvider, getVisibleError, handleWidgetUpdate, isSkipValidate, makeNewConfigUnique, renderForm, validateCustom, } from '../WidgetForm/Utils';
import { getFormLabelSuffix } from '../WidgetForm/Utils/getFormLabelSuffix';
import { showCropEditorDialog } from './Components/showCropEditorDialog';
import { Webcam } from './Components/Webcam';
import { type } from './types';
const WidgetFormImage = (props) => {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    const fileRef = React.useRef(null);
    const imageRef = React.useRef(null);
    const videoRef = React.useRef(null);
    const [showWebcam, setShowWebcam] = React.useState(false);
    const value = props.getDataStringOrUndefined();
    const error = getVisibleError(props);
    const formLabelSuffix = getFormLabelSuffix(props);
    const videoConstraints = {
        width: (_b = (_a = props.config.preview) === null || _a === void 0 ? void 0 : _a.maxWidth) !== null && _b !== void 0 ? _b : 400,
        height: (_d = (_c = props.config.preview) === null || _c === void 0 ? void 0 : _c.maxHeight) !== null && _d !== void 0 ? _d : 400,
        facingMode: 'user',
    };
    const clearImage = () => {
        if (fileRef.current) {
            fileRef.current.value = '';
        }
        props.setDataStringOrUndefined(undefined);
        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
    };
    const stopWebcam = () => {
        var _a;
        const stream = (_a = videoRef.current) === null || _a === void 0 ? void 0 : _a.stream;
        if (stream) {
            const tracks = stream.getTracks();
            tracks.forEach((track) => track.stop());
        }
        setShowWebcam(false);
    };
    const handleVideoToggle = () => {
        if (value) {
            clearImage();
        }
        if (showWebcam) {
            stopWebcam();
        }
        else {
            setShowWebcam(true);
        }
    };
    const showCropEditor = () => {
        if (!value) {
            return;
        }
        showCropEditorDialog({
            imageData: value,
            cropAspectRatio: props.config.cropAspectRatio,
            circularCrop: props.config.cropCircular,
            allowRotate: props.config.allowRotate,
            onSave: (data) => {
                props.setDataStringOrUndefined(data);
                renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                // clearing input after crop allows to upload same image again
                if (fileRef.current) {
                    fileRef.current.value = '';
                }
            },
        });
    };
    const readFile = (event) => {
        var _a, _b, _c, _d, _e, _f, _g;
        const fileInput = event.target;
        if (fileInput.files && fileInput.files[0]) {
            const inputFile = fileInput.files[0];
            const originalImage = new Image();
            const fileReader = new FileReader();
            if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.maxFileSize) && inputFile.size > props.config.validate.maxFileSize) {
                showErrorDialog({
                    title: (_b = props.config.validate.maxFileSizeMessage) !== null && _b !== void 0 ? _b : 'Uploaded file exceeds the maximum allowed size',
                });
                return;
            }
            if (((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.allowedFileTypes) &&
                ((_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.allowedFileTypes.length) > 0 &&
                !((_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.allowedFileTypes.some((fileType) => fileType.toLowerCase() === inputFile.type))) {
                showErrorDialog({
                    title: (_g = (_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.allowedFileTypesMessage) !== null && _g !== void 0 ? _g : `Not allowed file type`,
                });
                return;
            }
            fileReader.addEventListener('load', function () {
                if (fileReader.readyState === FileReader.DONE) {
                    const imageDataUrl = fileReader.result;
                    originalImage.addEventListener('load', () => {
                        var _a, _b, _c, _d, _e;
                        if (((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.allowedDimensions) &&
                            ((_b = props.config.validate) === null || _b === void 0 ? void 0 : _b.allowedDimensions.length) > 0 &&
                            !((_c = props.config.validate) === null || _c === void 0 ? void 0 : _c.allowedDimensions.find((d) => d.width === originalImage.width && d.height === originalImage.height))) {
                            showErrorDialog({
                                title: (_e = (_d = props.config.validate) === null || _d === void 0 ? void 0 : _d.allowedDimensionsMessage) !== null && _e !== void 0 ? _e : `Unsupported image size ${originalImage.width}x${originalImage.height}`,
                            });
                        }
                        else {
                            if (imageRef.current) {
                                imageRef.current.src = imageDataUrl;
                            }
                            props.setDataStringOrUndefined(imageDataUrl);
                            renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
                        }
                    });
                    originalImage.src = imageDataUrl;
                }
            });
            fileReader.readAsDataURL(inputFile);
            if (props.config.webcam && showWebcam) {
                setShowWebcam(false);
            }
        }
    };
    const handleWebcamError = (error) => {
        showErrorDialog({
            title: getErrorMessage(error),
        });
        setShowWebcam(false);
    };
    const captureWebcam = () => {
        if (!videoRef || !videoRef.current)
            return;
        const imageSrc = videoRef.current.getScreenshot();
        props.setDataStringOrUndefined(imageSrc !== null && imageSrc !== void 0 ? imageSrc : undefined);
        renderForm(props, { changed: true, autoSaveDebounce: props.config.autoSave });
    };
    const ariaDescribedBy = React.useId();
    return (React.createElement(FormControl, { fullWidth: true, required: (_e = props.config.validate) === null || _e === void 0 ? void 0 : _e.required, error: !!error, disabled: props.config.disabled },
        props.config.label && (React.createElement(FormLabel, { sx: { mb: 1 } }, formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label)),
        React.createElement(Box, { sx: { display: 'flex', flexDirection: 'column', width: '100%' } },
            React.createElement(FormTooltip, { tooltip: props.config.tooltip },
                React.createElement(Box, { sx: { display: 'flex', gap: 1 } },
                    React.createElement(Box, { component: 'label', "aria-label": 'Upload', onKeyDown: (event) => {
                            var _a;
                            if (['Enter', 'Space'].includes(event.code)) {
                                (_a = fileRef.current) === null || _a === void 0 ? void 0 : _a.click();
                            }
                        } },
                        React.createElement(Button, { component: 'span', disabled: props.config.disabled, variant: 'outlined', tabIndex: props.editMode ? -1 : undefined, "aria-describedby": ariaDescribedBy }, "Upload"),
                        React.createElement("input", { ref: fileRef, type: 'file', accept: ((_f = props.config.validate) === null || _f === void 0 ? void 0 : _f.allowedFileTypes) && props.config.validate.allowedFileTypes.length > 0
                                ? props.config.validate.allowedFileTypes.join(',')
                                : 'image/*', multiple: false, style: { display: 'none' }, onChange: readFile, "aria-describedby": ariaDescribedBy })),
                    props.config.webcam && (React.createElement(React.Fragment, null,
                        React.createElement(Button, { variant: 'outlined', disabled: props.config.disabled, onClick: handleVideoToggle, tabIndex: props.editMode ? -1 : undefined }, showWebcam ? 'Close webcam view' : ' Open webcam view'),
                        !value && showWebcam && (React.createElement(Button, { variant: 'outlined', disabled: props.config.disabled, onClick: captureWebcam, tabIndex: props.editMode ? -1 : undefined }, "Take picture")))),
                    value && (React.createElement(Button, { disabled: props.config.disabled, variant: 'outlined', onClick: clearImage, "data-test": 'clear-button', tabIndex: props.editMode ? -1 : undefined }, "Clear")),
                    value && props.config.allowEdit && (React.createElement(Button, { disabled: props.config.disabled, variant: 'outlined', onClick: showCropEditor, "data-test": 'edit-button', tabIndex: props.editMode ? -1 : undefined }, "Edit")))),
            React.createElement(FormHelperErrorDescription, { descriptionId: ariaDescribedBy, error: error, description: props.config.description })),
        React.createElement(Box, { sx: { mt: showWebcam || value ? 2 : 0 } },
            showWebcam && !value && (React.createElement(Webcam, { ref: videoRef, videoConstraints: videoConstraints, onUserMediaError: handleWebcamError, maxWidth: (_h = (_g = props.config.preview) === null || _g === void 0 ? void 0 : _g.maxWidth) !== null && _h !== void 0 ? _h : 400, maxHeight: (_k = (_j = props.config.preview) === null || _j === void 0 ? void 0 : _j.maxHeight) !== null && _k !== void 0 ? _k : 400, borderRadius: (_m = (_l = props.config.preview) === null || _l === void 0 ? void 0 : _l.borderRadius) !== null && _m !== void 0 ? _m : 0 })),
            value && (React.createElement(Box, { component: 'img', ref: imageRef, src: value, sx: {
                    maxWidth: (_p = (_o = props.config.preview) === null || _o === void 0 ? void 0 : _o.maxWidth) !== null && _p !== void 0 ? _p : 400,
                    maxHeight: (_r = (_q = props.config.preview) === null || _q === void 0 ? void 0 : _q.maxHeight) !== null && _r !== void 0 ? _r : 400,
                    borderRadius: (_t = (_s = props.config.preview) === null || _s === void 0 ? void 0 : _s.borderRadius) !== null && _t !== void 0 ? _t : 0,
                }, alt: formLabelSuffix ? props.config.label + formLabelSuffix : props.config.label }))),
        React.createElement(Box, null)));
};
const WidgetFormImageEditor = (props) => {
    return (React.createElement(BaseWidgetEditorComponent, Object.assign({}, props, { widgetLabelProvider: defaultWidgetLabelProvider, onEditWidget: (endEditing) => showWidgetEditDialog(props, endEditing, { title: 'Image: ' + props.config.label }, buildGeneralTab(props, {
            key: true,
            label: true,
            appendRowIndexToLabel: true,
            description: true,
            tooltip: true,
            children: [
                {
                    type: 'form.checkbox',
                    label: 'Use Webcam',
                    key: 'webcam',
                },
                {
                    type: 'form.checkbox',
                    label: 'Allow Edit',
                    key: 'allowEdit',
                },
                {
                    type: 'form.number',
                    label: 'Crop Aspect Ratio',
                    key: 'cropAspectRatio',
                },
                {
                    type: 'form.checkbox',
                    label: 'Allow Rotate',
                    key: 'allowRotate',
                },
                {
                    type: 'form.checkbox',
                    label: 'Circular Crop',
                    key: 'cropCircular',
                },
            ],
        }), buildValidateTab(props, {
            required: true,
            maxFileSize: true,
            children: [
                {
                    type: 'form.dataGrid',
                    label: 'Allowed Dimensions',
                    key: 'validate.allowedDimensions',
                    children: [
                        {
                            type: 'form.number',
                            key: 'height',
                            label: 'Height',
                        },
                        {
                            type: 'form.number',
                            key: 'width',
                            label: 'Width',
                        },
                    ],
                },
                {
                    type: 'form.text',
                    key: 'validate.allowedDimensionsMessage',
                    label: 'Allowed dimensions message',
                    emptyAsNull: true,
                },
            ],
        }), buildHiddenTab(props), buildStyleTab(props, {
            children: [
                {
                    type: 'form.panel',
                    label: 'Image preview',
                    children: [
                        {
                            type: 'form.number',
                            key: 'preview.borderRadius',
                            label: 'Preview Border Radius',
                        },
                        {
                            type: 'form.number',
                            key: 'preview.maxHeight',
                            label: 'Preview Height',
                        },
                        {
                            type: 'form.number',
                            key: 'preview.maxWidth',
                            label: 'Preview Width',
                        },
                    ],
                },
            ],
        }), buildEventTab(props, { eventOnChange: true, autoSave: true })) }),
        React.createElement(WidgetFormImage, Object.assign({}, props))));
};
export const widgetFormImage = {
    type,
    validate: (props) => {
        var _a, _b;
        if (isSkipValidate(props)) {
            return;
        }
        const value = props.getDataString();
        if ((_a = props.config.validate) === null || _a === void 0 ? void 0 : _a.required) {
            if (value.length === 0) {
                return (_b = props.config.validate.requiredMessage) !== null && _b !== void 0 ? _b : `${props.config.label} is required`;
            }
        }
        return validateCustom(props, value);
    },
    update: handleWidgetUpdate,
    component: WidgetFormImage,
    icon: React.createElement(ImageIcon, { fontSize: 'small' }),
    editorComponent: WidgetFormImageEditor,
    createWidget: (rootConfig, editorPath, widgets) => makeNewConfigUnique(rootConfig, editorPath, widgets, { type, key: 'image', label: 'Image' }),
};
